import { getDefaultStore } from "jotai";
import { authAtom } from "../Atoms";
import { Params, useParams } from "react-router-dom";

export const parseArray = <TValApi, TVal>( func: ( api: TValApi ) => TVal ) => ( x: TValApi[] ) => x.map( func );

export const getArgs = (): RequestInit => {
    const store = getDefaultStore();
    const auth = store.get( authAtom );
    const headers: HeadersInit = {};
    if( auth.token ) {
        headers['Authorization'] = `Bearer ${auth.token}`;
    }
    return {
        method: "GET",
        headers
    };
};

export const jsonArgs = ( verb: string, payload: any ): RequestInit => {
    const store = getDefaultStore();
    const auth = store.get( authAtom );
    const headers: HeadersInit = {
        "Content-Type": "application/json"
    };
    if( auth.token ) {
        headers['Authorization'] = `Bearer ${auth.token}`;
    }
    return {
        method: verb,
        headers,
        body: JSON.stringify( payload )
    };
};

export const postArgs = ( payload: any = {} ) => jsonArgs( "POST", payload );

export const fileArgs = ( files: File[] ): RequestInit => {
    const store = getDefaultStore();
    const auth = store.get( authAtom );
    const headers: HeadersInit = {};
    if( auth.token ) {
        headers['Authorization'] = `Bearer ${auth.token}`;
    }
    const formData = new FormData();
    files.forEach( file => formData.append( 'files', file ) );
    return {
        method:  "POST",
        headers: headers,
        body:    formData
    };
};

export interface OkResult<TVal> {
    isOk: true;
    value: TVal;
}

export interface ErrResult<TErr> {
    isOk: false;
    error: TErr;
}

export type Result<TErr, TVal> = OkResult<TVal> | ErrResult<TErr>;
export type OkHandler<T>  = ( val: T ) => void;
export type ErrHandler<T> = ( val: T ) => void;

export function parseString( val: string ) { return val; } 
export function identity<T>( val: T ) { return val; }

export const api = <TValApi, TVal>( url: string, init: RequestInit = {}, f: ( x: TValApi ) => TVal ): Promise<Result<string, TVal>> => {
    return fetch( `${url}`, init )
        .then( response => {
            const status = response.status;
            if( status === 401 ) {
                //not sure if this is the right way...need to think about it
                // getDefaultStore().set( authAtom, { isAuthenticated: false } );
                return { isOk: false, error: "Session expired" };
            }
            const isOk = ( response.status === 200 );
            if( !isOk ) {
                return { isOk: false, error: `Unexpected Response Code: ${response.status}` };
            }
            const json = response.json();
            return json.then( data => {
                const parsed = f( data );
                return { isOk: true, value: parsed };
            } );
        } );
};

export function paramIds( params: Params<string> ) {
    const res = Object.fromEntries( Object.entries( params ).map( ( [key, value] ) => {
        return [key, parseInt( value! )];
    } ) );
    return res;
}

export function optionalParamIds( params: Params<string> ) {
    const res = Object.fromEntries( Object.entries( params ).map( ( [key, value] ) => {
        return [key, value ? parseInt( value! ) : undefined];
    } ) );
    return res;
}

export function useParamIds() {
    const res = Object.fromEntries( Object.entries( useParams() ).map( ( [key, value] ) => {
        return [key, parseInt( value! )];
    } ) );
    return res;
}

export function useOptionalParamIds() {
    const res = Object.fromEntries( Object.entries( useParams() ).map( ( [key, value] ) => {
        return [key, value ? parseInt( value! ) : undefined];
    } ) );
    return res;
}

export interface LoaderError {
    header:  string;
    message: string;
}